import type { DifficultyLevel, Laterality, DomainEntity, DTOEntity } from "@akina/domain";
import type { PrescriptionStatusEnumApi } from "types/schemas";

export type TreatmentApplicationStatus = "pending" | "accepted" | "rejected";
export type Trend = "upward" | "downward" | "stable" | "unknown";

export const MIN_NUMBER_OF_SESSIONS_PER_WEEK = 3;

export type PrescriptionFileType = "image/jpeg" | "image/png" | "application/pdf";
export const PRESCRIPTION_ACCEPTED_FILE_TYPES: PrescriptionFileType[] = ["image/jpeg", "image/png", "application/pdf"];

export type TreatmentPhase = {
  startWeek: number;
  endWeek: number;
  key: string;
  scheduledStartDate?: Date;
  actualStartDate?: Date;
  actualEndDate?: Date;
};

export type TreatmentPhaseDTO = {
  start_week: number;
  end_week: number;
  key: string;
  scheduled_start_date?: string;
  actual_start_date?: string;
  actual_end_date?: string;
};

export type TreatmentPhasePlanDTO = {
  start_week: number;
  end_week: number;
  key: string;
};

export type TreatmentPhaseLogDTO = {
  phase_key: string;
  start_date: string | null;
  end_date: string | null;
};

export type TreatmentPhasesDTO = {
  current_phase_key: string;
  phase_plans: TreatmentPhasePlanDTO[];
  phase_logs: {
    [key: string]: TreatmentPhaseLogDTO;
  };
};

export type Treatment = {
  id: string;
  clinicId: string;
  therapistId: string;
  hasPrescription: boolean;
  bookedFirstSessionWithClinic: boolean;
  treatmentApplicationStatus: TreatmentApplicationStatus;
  treatmentTemplateKey: string;
  startDate: Date;
  scheduledEndDate: Date;
  endDate: Date | undefined;
  weekInTreatmentSinceStart: number;
  currentPhaseKey: string;
  currentPhaseWeek: number;
  phases: TreatmentPhase[];
  goal: string | undefined;
  prescriptionStatus: PrescriptionStatusEnumApi;
  nextPrescriptionStatus: PrescriptionStatusEnumApi;
} & DomainEntity;

export type TreatmentDTO = {
  id: string;
  organization_id: string;
  therapist_id: string;
  has_prescription: boolean;
  booked_first_session_with_clinic: boolean;
  treatment_application_status: TreatmentApplicationStatus;
  treatment_template_key: string;
  start_date: string;
  scheduled_end_date: string;
  end_date: string | undefined;
  week_in_treatment_since_start: number;
  current_phase_week: number;
  phases: TreatmentPhasesDTO;
  goal: string | undefined;
  prescription_status: PrescriptionStatusEnumApi;
  next_prescription_status: PrescriptionStatusEnumApi;
} & DTOEntity;

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

export type WeekDay = (typeof WEEKDAYS)[number];

export type PhaseLogs = {
  [key: string]: {
    phaseKey: string;
    startDate: Date | null;
    endDate: Date | null;
  };
};

export type PlannedTreatmentDTO = {
  treatment_id: string;
  treatment_template_key: string;
  session_key_sequence: string[];
  session_plans: PlannedSessionDTO[];
  last_update: Date;
  phases: TreatmentPhasesDTO;
  phase_key_allocation_to_session: string[];
  next_session_plan_key: string;
} & DTOEntity;
export type PlannedTreatment = {
  treatmentId: string;
  treatmentTemplateKey: string;
  sessionKeySequence: string[];
  sessionPlans: PlannedSession[];
  phases: TreatmentPhase[];
  lastUpdate: Date;
  phaseKeyAllocationToSession: string[];
  nextSessionPlanKey: string;
  phaseLogs?: PhaseLogs;
} & DomainEntity;

export type PlannedSessionDTO = {
  id: string;
  key: string;
  treatment_id: string;
  treatment_template_key: string;
  is_assessment: boolean;
  is_initial_session: boolean;
  default_instance_per_week: number;
  max_instance_per_week: number;
  earliest_possible_week: number;
  latest_possible_week: number;
  routine_plan_key_sequence: string[];
  routine_plans: PlannedRoutineDTO[];
  session_template_key: string;
} & DTOEntity;

export type PlannedSession = {
  id: string;
  key: string;
  treatmentId: string;
  treatmentTemplateKey: string;
  isAssessment: boolean;
  isInitialSession: boolean;
  defaultInstancePerWeek: number;
  maxInstancePerWeek: number;
  earliestPossibleWeek: number;
  latestPossibleWeek: number;
  routinePlanKeySequence: string[];
  routinePlans: PlannedRoutine[];
  sessionTemplateKey: string;
} & DomainEntity;

export type PlannedRoutineDTO = {
  id: number;
  treatment_id: string;
  treatment_template_key: string;
  key: string;
  has_laterality: boolean;
  laterality?: Laterality | null;
  n_set: number;
  n_repetitions: number | null;
  duration: string | null;
  break_duration: string | null;
  order: number;
  routine_template_key: string;
  exercise_template_key?: string;
  difficulty_level: DifficultyLevel;
} & DTOEntity;

export type PlannedRoutine = {
  id: number;
  treatmentId: string;
  treatmentTemplateKey: string;
  key: string;
  hasLaterality: boolean;
  laterality?: Laterality | null;
  nSet: number;
  nRepetitions: number | null;
  duration: string | null;
  breakDuration: string | null;
  order: number;
  routineTemplateKey: string;
  exerciseTemplateKey?: string;
  difficultyLevel: DifficultyLevel;
} & DomainEntity;

export type PlannedTreatmentResponseDTO = {
  current_phase_week: number;
  week_in_treatment_since_start: number;
  next_session_plan_key: string;
  phases: TreatmentPhasesDTO;
} & Omit<PlannedTreatmentDTO, "phases">;

export type PlannedTreatmentResponse = {
  currentPhaseWeek: number;
  currentPhaseKey: string;
  weekInTreatmentSinceStart: number;
  nextSessionPlanKey: string;
  phaseLogs?: PhaseLogs;
} & PlannedTreatment;

export type PhaseWithSessions = {
  key: string;
  phaseOrder: number;
  startWeek: number;
  endWeek: number;
  plannedSessions: PlannedSession[];
  scheduledStartDate?: Date;
  actualStartDate?: Date;
  actualEndDate?: Date;
};
